import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LocalPhoneRoundedIcon from "@mui/icons-material/LocalPhoneRounded";
import { EProductSection } from "../../../../../commons/interfaces/product";
import { BaseModalComponent } from "../../../../common/BaseModalComponent";
import { PlansLayout } from "../../../../layout/PlansLayout";
import { colors } from "../../../../ui/colors";
import { AppConfig } from "../../../interfaces/appIntegration.interfaces";
import { useOptionalFloatingBubbles } from "../../../../../context/floating-bubbles/FloatingBubblesContext";
import { useModals } from "src/context/ModalContext";
import { useNavigate } from "react-router-dom";

interface Props {
  app: AppConfig;
  refetch: () => void;
  details: {
    exists: boolean;
    status: "PENDING" | "COMPLETED" | null;
    externalUid: string;
    numberToPort: string | null;
  };
}

export const VrAssistantSetup = ({ app, details }: Props) => {
  const floatingBubbles = useOptionalFloatingBubbles();
  const openCalls = floatingBubbles?.openCalls;
  const { openModal } = useModals();
  const navigate = useNavigate();

  const isInstalled = Boolean(app.isInstalled);

  const canOpenFlowMobile =
    isInstalled && Boolean(details?.exists) && details?.status === "COMPLETED";

  const shouldShowConfigureNumber = isInstalled && !canOpenFlowMobile;

  const openPhoneNumbersConfiguration = () => {
    navigate("/business/organization");

    setTimeout(() => {
      openModal("Organization-PhoneNumbers");
    }, 500);
  };

  const getState = () => {
    if (!app.isInstalled) {
      return "REQUEST_ACCESS";
    }

    return "INSTALLED";
  };

  const state = getState();

  const stateConfig = {
    REQUEST_ACCESS: {
      label: "Request Access",
      color: colors.orange.base,
      hoverColor: colors.orange[600],
      helpText: "Opens a short request form. Approval is typically quick.",
      showModal: true,
      isClickable: true,
    },
    INSTALLED: {
      label: "Installed",
      color: "#10B981",
      hoverColor: "#10B981",
      helpText: "",
      showModal: false,
      isClickable: false,
    },
  };

  const currentState = stateConfig[state as keyof typeof stateConfig];

  const buttonStyles = {
    borderRadius: 2,
    py: 1.75,
    fontWeight: 900,
    textTransform: "none",
    bgcolor: currentState.color,
    color: "white",
    boxShadow: currentState.isClickable
      ? "0 10px 24px rgba(0,0,0,0.08)"
      : "none",
    transition:
      "transform 160ms ease, box-shadow 160ms ease, background 160ms ease",
    "&:hover": {
      bgcolor: currentState.hoverColor,
      transform: currentState.isClickable ? "translateY(-1px)" : "none",
      boxShadow: currentState.isClickable
        ? "0 14px 30px rgba(0,0,0,0.10)"
        : "none",
    },
    "&:active": {
      transform: "translateY(0px)",
    },
  };

  const getLineCardContent = () => {
    if (canOpenFlowMobile) {
      return {
        title: "Your Flow Mobile line is ready",
        description:
          "Your phone number setup has been completed successfully. You can now use Flow Mobile for calls and messages, or add another line when needed.",
        tone: "success" as const,
      };
    }

    if (details?.exists) {
      return {
        title: "Your Flow Mobile line is being configured",
        description:
          "Your Flow Mobile app is installed, but your phone line is not ready yet. Continue the number setup or add another line for your team.",
        tone: "warning" as const,
      };
    }

    return {
      title: "Configure your Flow Mobile phone line",
      description:
        "Your Flow Mobile app is installed, but calls and messages are not active yet. Configure your current plan by porting an existing number or selecting a new one.",
      tone: "warning" as const,
    };
  };

  const renderLineSetupCard = () => {
    if (!isInstalled) return null;

    const content = getLineCardContent();
    const isSuccess = content.tone === "success";

    return (
      <Paper
        elevation={0}
        sx={{
          border: isSuccess ? "1px solid #BBF7D0" : "1px solid var(--fx-surface-border, #E2E8F0)",
          borderRadius: 4,
          p: { xs: 2, md: 3 },
          background: isSuccess
            ? "linear-gradient(180deg, #F7FFF9 0%, #ECFDF3 100%)"
            : "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {!isSuccess && (
          <Box
            sx={{
              position: "absolute",
              right: -40,
              top: -40,
              width: 140,
              height: 140,
              borderRadius: "50%",
              background: "rgba(251, 142, 0, 0.07)",
            }}
          />
        )}

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2.5}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            sx={{ flex: 1 }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: isSuccess ? "var(--fx-colors-success-100, #D1FAE5)" : "rgba(251, 142, 0, 0.10)",
                color: isSuccess ? "#12B76A" : "#FB8E00",
                flexShrink: 0,
              }}
            >
              {isSuccess ? (
                <CheckCircleRoundedIcon />
              ) : (
                <LocalPhoneRoundedIcon />
              )}
            </Box>

            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 900,
                  color: "var(--fx-surface-text, #0F172A)",
                  mb: 0.75,
                }}
              >
                {content.title}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: isSuccess ? "var(--fx-surface-textsecondary, #64748B)" : "var(--fx-surface-textsecondary, #64748B)",
                  lineHeight: 1.75,
                  maxWidth: 760,
                }}
              >
                {content.description}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            sx={{
              width: { xs: "100%", md: "auto" },
              flexShrink: 0,
            }}
          >
            <BaseModalComponent
              defaultSizes={{ width: "1200", height: "1500" }}
              modalKey="Add-Flow-Mobile-Line"
              modalLabel="Add New Flow Mobile Line"
              elementTrigger={
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    minWidth: { xs: "100%", md: 180 },
                    borderRadius: 2.5,
                    px: 3,
                    py: 1.35,
                    textTransform: "none",
                    fontWeight: 800,
                    borderColor: "#FB8E00",
                    color: "#FB8E00",
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                    "&:hover": {
                      borderColor: "#E67F00",
                      bgcolor: "rgba(251, 142, 0, 0.08)",
                    },
                  }}
                >
                  Add new line
                </Button>
              }
            >
              <PlansLayout
                highlightCurrentPlan={false}
                subscriptionId={null}
                section={EProductSection.FLOW_MOBILE}
                title="Add a Flow Mobile Line"
                subtitle="Choose an additional Flow Mobile plan or line for your team."
                emptyTitle="You're on Our Best Plan"
                emptyDescription="Your team already has access to our full VoIP suite. There's nothing above this."
                emptyButtonLabel="Back to My Plan"
              />
            </BaseModalComponent>

            {shouldShowConfigureNumber && (
              <Button
                onClick={openPhoneNumbersConfiguration}
                variant="contained"
                size="large"
                sx={{
                  minWidth: { xs: "100%", md: 220 },
                  borderRadius: 2.5,
                  px: 3,
                  py: 1.35,
                  textTransform: "none",
                  fontWeight: 800,
                  bgcolor: "#FB8E00",
                  color: "#FFFFFF",
                  boxShadow: "0 1px 4px rgba(251, 142, 0, 0.16)",
                  "&:hover": {
                    bgcolor: "#E67F00",
                    boxShadow: "0 2px 6px rgba(251, 142, 0, 0.22)",
                  },
                }}
              >
                Configure your number
              </Button>
            )}

            {canOpenFlowMobile && (
              <Button
                onClick={openCalls}
                variant="contained"
                size="large"
                sx={{
                  minWidth: { xs: "100%", md: 220 },
                  borderRadius: 2.5,
                  px: 3,
                  py: 1.35,
                  textTransform: "none",
                  fontWeight: 700,
                  bgcolor: "#12B76A",
                  boxShadow: "0 1px 4px rgba(18, 183, 106, 0.12)",
                  "&:hover": {
                    bgcolor: "#1B8A5A",
                    boxShadow: "0 2px 6px rgba(18, 183, 106, 0.16)",
                  },
                }}
              >
                Open Flow Mobile
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    );
  };

  return (
    <Stack spacing={2}>
      {currentState.showModal ? (
        <BaseModalComponent
          defaultSizes={{ width: "1200", height: "1500" }}
          modalKey="Request-Access-Phone"
          modalLabel="Request Access Phone"
          elementTrigger={
            <Button fullWidth size="large" variant="contained" sx={buttonStyles}>
              {currentState.label}
            </Button>
          }
        >
          <PlansLayout
            highlightCurrentPlan={false}
            subscriptionId={null}
            section={EProductSection.FLOW_MOBILE}
            title="Get Flow Mobile"
            subtitle="Professional VoIP plans for teams that can't afford to miss a call. Pay monthly, cancel anytime."
            emptyTitle="You're on Our Best Plan"
            emptyDescription="Your team already has access to our full VoIP suite. There's nothing above this."
            emptyButtonLabel="Back to My Plan"
          />
        </BaseModalComponent>
      ) : (
        <Button
          fullWidth
          size="large"
          variant="contained"
          onClick={(event) =>
            !currentState.isClickable && event.preventDefault()
          }
          sx={buttonStyles}
        >
          {currentState.label}
        </Button>
      )}

      {!!currentState.helpText && (
        <Typography
          variant="caption"
          sx={{
            color: colors.gray[400],
            lineHeight: 1.7,
            textAlign: "center",
            px: 1,
          }}
        >
          {currentState.helpText}
        </Typography>
      )}

      {renderLineSetupCard()}
    </Stack>
  );
};